import AppearanceSettingCard from '../components/settings/AppearanceSettingCard';
import DisplayModeSettingCard from '../components/settings/DisplayModeSettingCard';
import { API_BASE } from '../lib/config';
import React, { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { accountTeamRoleLabel } from '../lib/accountContexts';
import AccountTeamsCard, { AccountSecurityCard } from '../components/settings/AccountTeamsCard';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { getInitials } from '../lib/utils';
import { User, Mail, Shield, Settings as SettingsIcon, MessageSquare, Users, ChevronRight, Camera, Trash2, Loader2, Pencil, Phone, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import NotificationSettings from '../components/NotificationSettings';
import LanguageSettingCard from '../components/settings/LanguageSettingCard';
import { LoginInfoCard } from '../components/settings/LoginInfoCard';
import { LoginMethodsCard } from '../components/settings/LoginMethodsCard';
import DetailPageHeader from '../components/shared/DetailPageHeader';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { updateUser } from '../lib/api';
import { APP_VERSION, BUILD_TIME, BUILD_ID } from '../lib/version';
import './account-design.css';

const API = API_BASE;

export default function Settings() {
  const { t } = useTranslation(['settings', 'navigation', 'profile', 'common', 'development']);
  const { user, isCoach, activeTeam, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const accountTabs = [
    { id: 'account-profile', label: 'Profil', icon: User },
    { id: 'account-login', label: 'Inloggning', icon: Mail },
    { id: 'account-teams', label: 'Lag och barn', icon: Users },
    { id: 'account-security', label: 'Säkerhet', icon: Shield },
    { id: 'account-preferences', label: 'Appinställningar', icon: SettingsIcon },
  ];
  const requestedTab = location.hash.slice(1);
  const activeTab = accountTabs.some(tab => tab.id === requestedTab) ? requestedTab : 'account-profile';
  const handleTabChange = (id) => {
    navigate({ pathname: location.pathname, search: location.search, hash: `#${id}` }, { replace: true, preventScrollReset: true });
  };
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '', phone: ''
  });

  const startEditProfile = () => {
    setProfileForm({
      name: user?.name || '',
      phone: user?.phone || ''
    });
    setEditingProfile(true);
  };

  const cancelEditProfile = () => {
    setEditingProfile(false);
  };

  const handleSaveProfile = async () => {
    if (!profileForm.name?.trim()) {
      toast.error(t('profile:errors.required'));
      return;
    }
    try {
      setSavingProfile(true);
      await updateUser(user.id, {
        name: profileForm.name.trim(),
        phone: profileForm.phone?.trim() || undefined
      });
      toast.success(t('profile:toasts.profile_saved'));
      setEditingProfile(false);
      if (refreshUser) await refreshUser();
    } catch (error) {
      const detail = error.response?.data?.detail || t('profile:toasts.profile_save_failed');
      toast.error(detail);
    } finally {
      setSavingProfile(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.match(/^image\/(jpeg|png|webp|svg\+xml)$/)) {
      toast.error(t('settings:avatar.invalid_type', { defaultValue: 'Invalid file type. Use PNG, JPG, WebP or SVG' }));
      return;
    }

    // Validate file size (2 MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(t('settings:avatar.too_large', { defaultValue: 'File is too large (max 2 MB)' }));
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/media/users/avatar`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success(t('profile:toasts.photo_uploaded'));
      if (refreshUser) await refreshUser();
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(error.response?.data?.detail || t('profile:toasts.photo_upload_failed'));
    } finally {
      setUploading(false);
      // Clear input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteAvatar = async () => {
    if (!user?.avatar_url) return;

    try {
      setUploading(true);
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/media/users/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success(t('profile:toasts.photo_removed'));
      if (refreshUser) await refreshUser();
    } catch (error) {
      console.error('Error deleting avatar:', error);
      toast.error(t('profile:toasts.photo_upload_failed'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Tabs value={activeTab} onValueChange={handleTabChange} className="min-w-0 bg-[rgb(var(--tn-bg-0d0f13))] pb-3" data-testid="settings-page">
      <div className="-mt-3">
        <DetailPageHeader
          title="Mitt konto"
          subtitle="Din profil, inloggning och dina inställningar"
          testId="settings-detail-header"
          tabs={
            <TabsList aria-label="Kontots avsnitt" className="flex h-11 w-full justify-start gap-1 overflow-x-auto rounded-none bg-transparent px-2 py-0 sm:px-5">
              {accountTabs.map(({ id, label, icon: Icon }) => (
                <TabsTrigger
                  key={id}
                  value={id}
                  data-testid={`settings-tab-${id}`}
                  className="relative h-11 shrink-0 rounded-none px-3.5 py-0 text-[12px] font-medium text-[rgb(var(--tn-text-a6afba))] shadow-none hover:bg-[rgb(var(--tn-tint-ffffff))]/[0.035] hover:text-[rgb(var(--tn-text-f2f4f7))] data-[state=active]:bg-transparent data-[state=active]:text-[var(--t-accent)] data-[state=active]:shadow-none data-[state=active]:after:absolute data-[state=active]:after:bottom-0 data-[state=active]:after:left-3 data-[state=active]:after:right-3 data-[state=active]:after:h-[2px] data-[state=active]:after:rounded-full data-[state=active]:after:bg-[var(--tn-accent-fill)] focus-visible:ring-inset"
                >
                  <Icon className="mr-2 h-4 w-4" />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          }
        />
      </div>
      <div className="tn-account-content mt-[13px] w-full min-w-0 max-w-5xl [&>*]:min-w-0">
          {/* Keep panels mounted so switching tabs preserves unfinished forms. */}
          <TabsContent forceMount value="account-profile" className="mt-0 space-y-3 data-[state=inactive]:hidden">
          <p className="text-sm text-muted-foreground">Dina personuppgifter gäller för hela kontot. Roller och åtkomst visas för respektive lag.</p>
          {/* Profile Card */}
          <Card className="bg-card border-border">
            <CardHeader>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CardTitle className="font-heading flex items-center gap-2">
                  <User className="w-5 h-5" />
                  {t('settings:cards.profile_title')}
                </CardTitle>
                {!editingProfile ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs"
                    onClick={startEditProfile}
                    data-testid="edit-profile-btn"
                  >
                    <Pencil className="w-3 h-3 mr-1" />
                    {t('profile:actions.edit_profile')}
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 text-xs"
                      onClick={cancelEditProfile}
                      disabled={savingProfile}
                    >
                      <X className="w-3 h-3 mr-1" />
                      {t('profile:actions.cancel')}
                    </Button>
                    <Button
                      size="sm"
                      className="h-8 text-xs"
                      onClick={handleSaveProfile}
                      disabled={savingProfile || !profileForm.name?.trim()}
                      data-testid="save-profile-btn"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      {savingProfile ? t('profile:loading.saving') : t('profile:actions.save_changes').split(' ')[0]}
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-4">
                {/* Avatar with upload capability */}
                <div className="relative group">
                  <Avatar className="h-20 w-20">
                    {user?.avatar_url && (
                      <AvatarImage 
                        src={`${API}${user.avatar_url}`} 
                        alt={user?.name}
                        className="object-cover"
                      />
                    )}
                    <AvatarFallback className="bg-primary/20 text-primary font-bold text-2xl">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  
                  {/* Upload overlay */}
                  <div 
                    className="absolute inset-0 bg-black/60 rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {uploading ? (
                      <Loader2 className="w-6 h-6 text-[rgb(var(--tn-text-ffffff))] animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6 text-[rgb(var(--tn-text-ffffff))]" />
                    )}
                  </div>
                  
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/svg+xml"
                    className="hidden"
                    onChange={handleAvatarUpload}
                    disabled={uploading}
                  />
                </div>
                
                <div className="min-w-0 flex-1">
                  <h3 className="text-xl font-semibold text-[rgb(var(--tn-text-ffffff))] break-words">{user?.name}</h3>
                  <Badge className={isCoach ? 'bg-accent/20 text-accent' : 'bg-primary/20 text-primary'}>
                    {accountTeamRoleLabel(user, activeTeam) || 'Mitt konto'}
                  </Badge>
                  
                  {activeTeam?.name && <p className="mt-1 text-xs text-muted-foreground break-words">Rollen ovan gäller i {activeTeam.name}.</p>}
                  {/* Avatar actions */}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Camera className="w-3 h-3 mr-1" />
                      {user?.avatar_url ? t('profile:actions.upload_photo') : t('profile:actions.upload_photo')}
                    </Button>
                    {user?.avatar_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs text-destructive hover:text-destructive"
                        onClick={handleDeleteAvatar}
                        disabled={uploading}
                      >
                        <Trash2 className="w-3 h-3 mr-1" />
                        {t('profile:actions.remove_photo')}
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {editingProfile ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground font-medium">{t('profile:fields.name')} *</label>
                    <Input
                      data-testid="profile-edit-name"
                      value={profileForm.name}
                      onChange={(e) => setProfileForm({ ...profileForm, name: e.target.value })}
                      placeholder={t('profile:fields.name')}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground">Inloggningsadressen ändrar du under <button type="button" onClick={() => handleTabChange('account-login')} className="text-primary underline">Inloggning</button>, där den nya adressen verifieras.</p>
                  <div className="space-y-2">
                    <label className="text-xs text-muted-foreground font-medium">{t('profile:fields.phone')}</label>
                    <Input
                      data-testid="profile-edit-phone"
                      value={profileForm.phone}
                      onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                      placeholder="+46 7X-XXX XX XX"
                    />
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                    <Mail className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">{t('profile:fields.email')}</p>
                      <p className="text-[rgb(var(--tn-text-ffffff))]">{user?.email || (user?.username ? 'Inloggning med användarnamn' : 'Ingen e-postadress')}</p>
                    </div>
                  </div>

                  {user?.phone && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <Phone className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('profile:fields.phone')}</p>
                        <p className="text-[rgb(var(--tn-text-ffffff))]">{user.phone}</p>
                      </div>
                    </div>
                  )}

                  {user?.position && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <Shield className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <p className="text-xs text-muted-foreground">{t('common:position', { defaultValue: 'Position' })}</p>
                        <p className="text-[rgb(var(--tn-text-ffffff))]">{user.position}</p>
                      </div>
                    </div>
                  )}

                  {user?.jersey_number && (
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                      <span className="w-5 h-5 text-center text-muted-foreground font-bold">#</span>
                      <div>
                        <p className="text-xs text-muted-foreground">{t('common:jersey_number', { defaultValue: 'Tröjnummer' })}</p>
                        <p className="text-[rgb(var(--tn-text-ffffff))]">{user.jersey_number}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          </TabsContent>
          <TabsContent forceMount value="account-login" className="mt-0 data-[state=inactive]:hidden">
            <LoginInfoCard />
          <LoginMethodsCard />
          </TabsContent>
          <TabsContent forceMount value="account-teams" className="mt-0 data-[state=inactive]:hidden">
            <AccountTeamsCard />
          </TabsContent>
          <TabsContent forceMount value="account-security" className="mt-0 data-[state=inactive]:hidden">
            <AccountSecurityCard />
          </TabsContent>
          <TabsContent forceMount value="account-preferences" className="mt-0 space-y-4 data-[state=inactive]:hidden">
          <AppearanceSettingCard />
          <DisplayModeSettingCard />
          <LanguageSettingCard />

          {/* Push Notifications */}
          <NotificationSettings />

          {/* Feedback & Support */}
          <Card 
            className="bg-card border-border cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => navigate('/feedback')}
            data-testid="feedback-support-card"
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[rgb(var(--tn-text-ffffff))]">{t('settings:feedback.title', { defaultValue: 'Feedback & Support' })}</h3>
                    <p className="text-sm text-muted-foreground">{t('settings:feedback.subtitle', { defaultValue: 'Report a problem or suggest an improvement' })}</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </div>
            </CardContent>
          </Card>

          {/* App Info */}
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="font-heading">{t('settings:cards.about_title', { defaultValue: 'About' })}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p><strong className="text-[rgb(var(--tn-text-ffffff))]">{t('settings:about.version', { defaultValue: 'Version' })}:</strong> {APP_VERSION}</p>
                <p><strong className="text-[rgb(var(--tn-text-ffffff))]">{t('settings:about.updated', { defaultValue: 'Last updated' })}:</strong> {BUILD_TIME}</p>
                <p className="text-[11px] text-muted-foreground/55 break-all" data-testid="about-build-id">Build: {BUILD_ID}</p>
                <p><strong className="text-[rgb(var(--tn-text-ffffff))]">{t('settings:about.app', { defaultValue: 'App' })}:</strong> {t('common:brand.name')}</p>
                <p className="pt-2">
                  {t('common:brand.name')} – {t('common:brand.tagline')} {t('settings:about.description', { defaultValue: 'Complete team platform for football teams with training, matches, statistics and communication.' })}
                </p>
              </div>
            </CardContent>
          </Card>
          </TabsContent>
      </div>
    </Tabs>
  );
}
